import React, { ForwardRefExoticComponent, SVGProps } from 'react'

import {
  FormsElementInput,
  FormsElementParamsInput,
  FormsElementTypeEnum,
} from 'generated/resources/datatable.graphql'
import { v4 as uuid } from 'uuid'

import i18n from 'modules/core/lib/i18n'

import {
  Answer,
  ElementData,
  ElementOption,
  booleanParam,
  elementParam,
  numberParam,
} from 'modules/forms/lib/types'

/**
 * Base element class that all element types will extend.
 * Contains common attributes and methods for all element types.
 */
export abstract class Element {
  /**
   * uuid to be able to work with unsaved elements. This ID is generated only in memory,
   * not persisted in database. The same form, loaded multiple times creates different uuids
   * for the same elements
   */
  uuid: string

  /**
   * Unique identifier for the element
   */
  id: string

  /**
   * The label of the element
   */
  label: string

  /**
   * The description of the element
   */
  description: string

  /**
   * The position of the element in the form or in the section
   */
  position: number

  /**
   * The section of the element
   */
  sectionId: string | undefined

  /**
   * Whether the element is mandatory to answer
   */
  mandatory: boolean

  /**
   * Custom parameters specific to the element
   */
  params: FormsElementParamsInput

  /**
   * Options for this element
   */
  options: ElementOption[]

  /**
   * The answer of the element
   */
  answer: Answer | null

  /**
   * Whether the element has a correct answer defined
   */
  hasCorrectAnswer: boolean

  /**
   * The updated date of the element
   */
  updatedAt: string | undefined

  /**
   * The created date of the element
   */
  createdAt: string | undefined

  /**
   * Constructor for the Element class
   */
  constructor(data: ElementData) {
    const {
      label,
      description = '',
      id = '',
      position = 0,
      mandatory = false,
      params = {},
      options = [],
      sectionId = undefined,
      answer = null,
      hasCorrectAnswer = false,
    } = data
    this.uuid = uuid()
    this.id = id
    this.label = label
    this.description = description
    this.position = position
    this.mandatory = mandatory
    this.params = params
    this.options = options
    this.sectionId = sectionId
    this.answer = answer
    this.hasCorrectAnswer = hasCorrectAnswer
  }

  /**
   * Get the name of the element
   */
  abstract getName(): string

  /**
   * Get the type of the element
   */
  abstract getType(): FormsElementTypeEnum

  /**
   * Get the icon of the element
   */
  abstract getIcon(): ForwardRefExoticComponent<SVGProps<SVGSVGElement>>

  /**
   * Validate the element's configuration
   */
  validate(): Record<string, string> {
    const errors: Record<string, string> = {}

    if (!this.label.trim()) {
      errors.label = i18n.t('forms.editor.validation.title_required')
    } else if (this.label.length > 255) {
      // this is the database size
      errors.label = i18n.t('forms.editor.validation.title_maxlength')
    }
    return errors
  }

  /**
   * Get a React component for editing this element
   *
   * @param _onChange - Callback function to handle changes to the element
   * @returns A React component for editing this element
   */

  /**
   * Get a React component for editing this element
   *
   * @param onChange - Callback function to handle changes to the element
   * @returns A React component for editing this element
   */
  getEditorComponent(_onChange: (element: Element) => void): React.ReactNode {
    return null
  }

  /**
   * Get a React component to preview this element
   *
   * @param disabled - Disable the component functionality
   * @param showCorrectAnswerIcons - Show correct/incorrect icons for questions with answers
   * @returns A React component  to preview this element
   */
  getPreviewComponent(_disabled?: boolean, _showCorrectAnswerIcons?: boolean): React.ReactNode {
    return null
  }

  getQuestionComponent(_onChange: (answer: Answer) => void): React.ReactNode {
    return null
  }

  /**
   * Return the element data to create or update a form
   *
   * Note: It does not have the 'sectionId' field because when the element
   * is sent to create or update a form, the elements inside a section
   * are nested, so it's not needed
   */
  toBackend(): FormsElementInput {
    return {
      id: this.id || undefined,
      label: this.label,
      description: this.description,
      type: this.getType(),
      position: this.position,
      mandatory: this.mandatory,
      params: this.params,
      options: this.options.length ? this.options : undefined,
      hasCorrectAnswer: this.hasCorrectAnswer,
    }
  }

  configureParams(): elementParam[] {
    return []
  }

  static booleanParam(name: string, label: string): booleanParam {
    return {
      type: 'bool',
      name: name,
      label: label,
    }
  }

  static numberParam(name: string, label: string): numberParam {
    return {
      type: 'number',
      name: name,
      label: label,
    }
  }

  addOption(option: ElementOption): void {
    this.options.push(option)
  }

  setOptions(options: string[]): void {
    this.options = options.map((label) => {
      return { value: '', label: label, isCorrect: false }
    })
  }

  lock(): this {
    this.params.locked = true
    return this
  }

  isLocked(): boolean {
    return this.params.locked ?? false
  }

  /**
   * Check if this element type has options that can be preserved when changing element types.
   * Elements with options are defined in the typeHasOptions method.
   */
  hasOptions(): boolean {
    return Element.typeHasOptions(this.getType())
  }

  /**
   * Check if an element type has options that can be preserved when changing element types.
   * @param type - The element type to check
   * @returns true if the element type has options
   */
  static typeHasOptions(type: FormsElementTypeEnum): boolean {
    return type === 'select' || type === 'multiselect' || type === 'dropdownsingle'
  }
}
